import { useMemo } from "react";
import classNames from "classnames";
import { Link, useParams } from "react-router-dom";
import ChampionCounter from "@ugg/shared/components/common/Tooltips/ChampionCounter";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { buildQueryParams } from "@ugg/shared/utils/url-query-params";
import { useQueryString } from "@ugg/shared/hooks/use-query-string";
import { normalizeRole, RoleS } from "@ugg/shared/utils/role-helpers";
import { useValidateQueryParams } from "@ugg/shared/query-params/params-helpers";
import { validParams } from "@ugg/shared/query-params/valid-params";
import { getChampionBuildUrl } from "@ugg/shared/routes/app-routes";
import { CHAMPION_PAGES } from "@ugg/shared/pages/champion-pages";
import { useTierListPage } from "@ugg/shared/pages/tier-list-pages";
import { SPECIAL_GAME_MODES } from "@ugg/shared/utils/special-game-modes";

interface FriendlyItemProps {
  params: {
    idx: number;
    displayWinRate: string;
    displayWinRateMini: string;
    isGray: boolean;
    curChampionId: number;
    secChampionId: number;
    datum: {
      type?: string;
      champion_id: number;
      wins: number;
      matches: number;
      win_rate: number;
      sec_win_rate: number;
    };
    role?: string;
    dim: number;
  };
}

const FriendlyItem = (props: FriendlyItemProps) => {
  const { getNormalizedChampionName } = getRiotAssetsContext();
  const params = useQueryString();
  const { gameMode } = useParams<{ gameMode?: string }>();
  const validateQueryParams = useValidateQueryParams(validParams);
  const { isGray, secChampionId, role, dim } = props.params;
  const tierListPage = useTierListPage();
  const specialGameMode = Object.values(SPECIAL_GAME_MODES).find((gameMode) => gameMode.tierListPageName === tierListPage);

  const normalizedChampionName = getNormalizedChampionName(secChampionId);
  const currentFilters = { ...params, role: normalizeRole(role || "", RoleS.ALL) };
  const validatedParams = validateQueryParams(specialGameMode?.buildPageName || CHAMPION_PAGES.OVERVIEW, currentFilters);
  let pathname = getChampionBuildUrl(normalizedChampionName);
  if (specialGameMode?.getSpecialGameModeBuildUrl) {
    pathname = specialGameMode?.getSpecialGameModeBuildUrl(normalizedChampionName);
  }

  return (
    <Link
      className={classNames({ ["grayscale-[100%] opacity-[0.3]"]: isGray })}
      to={{
        pathname,
        search: buildQueryParams(validatedParams),
      }}
    >
      <div
        className={`
        _champion-face relative w-[24px] h-[24px] rounded-[3px] max-xs:w-[18px] max-xs:h-[18px] overflow-hidden
        [&>div]:absolute [&>div]:top-[50%] [&>div]:left-[50%] [&>div]:translate-x-[-50%] 
        [&>div]:translate-y-[-50%] [&>div]:scale-[1.1]
      `}
      >
        <ChampionCounter dim={dim} data={props.params} />
      </div>
    </Link>
  );
};

interface FriendlyProps {
  data: any;
  championId: number;
  role?: string;
  dim: number;
  showCount: number;
}

export const Friendly = (props: FriendlyProps) => {
  const { data, championId, role, dim, showCount } = props;

  const showMax = showCount || 7;
  const champs = useMemo(() => {
    const list = [];
    for (let i = 0; i < showMax; i++) {
      if (data.length > i) {
        const curData = data[i];
        const secWinRate = curData.win_rate;
        const displayWinRate = secWinRate.toFixed(1);
        const displayWinRateMini = secWinRate.toFixed(0);
        const isGray = displayWinRate > 50;

        const params = {
          datum: { ...curData, type: "team" },
          idx: i,
          isGray,
          displayWinRate,
          displayWinRateMini,
          curChampionId: championId,
          secChampionId: curData.champion_id,
          role,
          dim,
        };

        list.push(<FriendlyItem key={i} params={params} />);
      } else {
        list.push(<div key={i} className="relative w-[24px] h-[24px] rounded-[3px] max-xs:w-[18px] max-xs:h-[18px]" />);
      }
    }

    return list;
  }, [data]);

  return <div className="flex items-center gap-[6px]">{champs}</div>;
};
